import { useQuery } from '@tanstack/react-query'

import {
  ProgramInsightQueryParams,
  ProgramInsightsAnnotationResponse,
  ReactQueryHttpError,
  VqaResponse,
} from '@sparkai/shared-types'
import { getTimezonePreferenceQueryKeyPart, type AppTimezonePreference } from '@sparkai/utils'

import { apiClient } from '../../apiClient'

const CACHE_KEY_PREPEND = 'program-insights'

type TimezoneAwareProgramInsightQueryParams = ProgramInsightQueryParams & {
  timezonePreference?: AppTimezonePreference
}

const getTimezoneQueryKeyPart = (timezonePreference?: AppTimezonePreference) =>
  timezonePreference ? [getTimezonePreferenceQueryKeyPart(timezonePreference)] : []

const cleanParams = <T extends object>(params: T) => {
  const cleaned: Partial<T> = { ...params }

  for (const key in cleaned) {
    const typedKey = key as keyof T
    const value = cleaned[typedKey]
    if (value === undefined || value === null || value === '') {
      // eslint-disable-next-line @typescript-eslint/no-dynamic-delete
      delete cleaned[typedKey]
    }
  }

  return cleaned
}

export function useGetAnnotationInsights(params: TimezoneAwareProgramInsightQueryParams) {
  const { timezonePreference, ...requestParams } = params

  return useQuery<ProgramInsightsAnnotationResponse, ReactQueryHttpError>({
    queryKey: [
      `${CACHE_KEY_PREPEND}-annotation`,
      requestParams,
      ...getTimezoneQueryKeyPart(timezonePreference),
    ],
    queryFn: async () => {
      const response = await apiClient.get(`/int/v3/programs/${params.programId}/insights/annotations`, {
        params: cleanParams(requestParams),
      })
      return response.data
    },
    enabled: Boolean(params.programId),
    refetchOnWindowFocus: false,
  })
}

interface ProgramInsightVqaQueryParams {
  programId: string
  granularity: 'daily' | 'weekly' | 'monthly'
  startDate?: string
  endDate?: string
  systemId?: string
  softwareVersion?: string
  timezonePreference?: AppTimezonePreference
}

export function useGetVqaInsights(params: ProgramInsightVqaQueryParams) {
  const { timezonePreference, ...requestParams } = params

  return useQuery<VqaResponse, ReactQueryHttpError>({
    queryKey: [
      `${CACHE_KEY_PREPEND}-vqa-report`,
      requestParams,
      ...getTimezoneQueryKeyPart(timezonePreference),
    ],
    queryFn: async () => {
      const response = await apiClient.get(`/int/v3/programs/${params.programId}/insights/vqa`, {
        params: cleanParams(requestParams),
      })

      return response.data
    },
    enabled: Boolean(params.programId),
    retry: false,
    refetchOnWindowFocus: false,
  })
}
